// src/components/assistant/ModelPicker.tsx
//
// Selector compacto de modelo y esfuerzo del chat (estilo ChatGPT). «Auto» por
// defecto = lo que decide la org. La lista viene del backend (GET /threads/models)
// ya filtrada por org, llaves y candado soberano; aquí solo se pinta.
//
// Accesible: botón con aria-haspopup/aria-expanded; menú con role="menu" e ítems
// menuitemradio; ↑/↓/Inicio/Fin para moverse, Enter/Espacio para elegir, Esc o
// Tab para cerrar (Esc devuelve el foco al botón). Tokens semánticos → claro/oscuro.

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown, Gauge, Sparkles } from 'lucide-react';
import type { ChatModelChoice, ChatModelComingSoon, ChatModelOption, ChatModelsInfo } from '@/lib/api';
import { ProviderLogo } from './ProviderLogo';

const FALLBACK_GROUP: Record<string, string> = {
  openai: 'OpenAI', anthropic: 'Anthropic', xai: 'Grok', google: 'Gemini', totbi: 'U-CogNet',
};

interface ModelGroup {
  key: string;
  label: string;
  provider: string;
  models: ChatModelOption[];
  soon: ChatModelComingSoon[];
}

/** Modelos agrupados por proveedor en el orden que manda el backend. */
export function groupModels(info: ChatModelsInfo): ModelGroup[] {
  // 🔒 Org soberana: el backend ya manda solo el modelo gobernado; aquí además no se
  // pinta ningún proveedor externo (ni como grupo vacío ni como «Próximamente»).
  if (info.sovereign) info = sovereignOnly(info);
  const groups = new Map<string, ModelGroup>();
  const ensure = (provider: string, label: string) => {
    if (!groups.has(label)) groups.set(label, { key: label, label, provider, models: [], soon: [] });
    return groups.get(label)!;
  };
  for (const g of info.groups ?? []) ensure(g.provider, g.group);
  for (const m of info.models) ensure(m.provider, m.group || FALLBACK_GROUP[m.provider] || m.provider || 'Otros').models.push(m);
  for (const c of info.coming_soon ?? []) ensure(c.provider, c.group).soon.push(c);
  return Array.from(groups.values()).filter((g) => g.models.length || g.soon.length);
}

/** Proveedores de modelo GOBERNADO (soberano). `totbi` = U-CogNet. */
export const SOVEREIGN_PROVIDERS = new Set(['totbi']);

/** Defensa en profundidad para orgs soberanas: deja solo modelos de proveedor
 *  gobernado, sin grupos de orden ni «Próximamente». Pura. */
export function sovereignOnly(info: ChatModelsInfo): ChatModelsInfo {
  return {
    ...info,
    models: info.models.filter((m) => SOVEREIGN_PROVIDERS.has(m.provider)),
    groups: [],
    coming_soon: [],
  };
}

interface ModelPickerProps {
  info: ChatModelsInfo;
  value: ChatModelChoice;
  onChange: (choice: ChatModelChoice) => void;
  disabled?: boolean;
  /** Abre el menú ya desplegado (solo para la página de desarrollo/capturas). */
  defaultOpen?: boolean;
  /** Solo ícono a cualquier ancho (chat flotante). */
  compact?: boolean;
}

export function modelChoiceLabel(info: ChatModelsInfo, value: ChatModelChoice): string {
  if (!value.model || value.model === 'auto') return info.auto.label || 'Auto';
  const m = info.models.find((x) => x.id === value.model);
  if (!m) return info.auto.label || 'Auto';
  const e = value.effort ? m.efforts.find((x) => x.id === value.effort) : null;
  return e ? `${m.label} · ${e.label}` : m.label;
}

export function ModelPicker({ info, value, onChange, disabled = false, defaultOpen = false, compact = false }: ModelPickerProps) {
  const [open, setOpen] = useState(defaultOpen);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();

  const isAuto = !value.model || value.model === 'auto';
  const current = isAuto ? null : info.models.find((m) => m.id === value.model) || null;
  const efforts = current?.efforts ?? [];
  const label = modelChoiceLabel(info, value);
  // En móvil, solo el nombre del modelo (el esfuerzo se ve al abrir el menú).
  const shortLabel = current ? current.label : info.auto.label || 'Auto';

  const items = useCallback(
    () => Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? []),
    [],
  );

  const close = useCallback((focusButton: boolean) => {
    setOpen(false);
    if (focusButton) btnRef.current?.focus();
  }, []);

  // Al abrir, el foco va al ítem marcado (o al primero).
  useEffect(() => {
    if (!open || defaultOpen) return;
    const list = items();
    (list.find((el) => el.getAttribute('aria-checked') === 'true') ?? list[0])?.focus();
  }, [open, items, defaultOpen]);

  // Clic fuera cierra.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const list = items();
    const idx = list.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = e.key === 'ArrowDown' ? (idx + 1) % list.length : (idx - 1 + list.length) % list.length;
      list[next]?.focus();
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      (e.key === 'Home' ? list[0] : list[list.length - 1])?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
    } else if (e.key === 'Tab') {
      close(false);
    }
  };

  const pickModel = (id: string) => {
    if (id === 'auto') {
      onChange({ model: 'auto', effort: null });
      close(true);
      return;
    }
    const m = info.models.find((x) => x.id === id);
    if (!m) return;
    const keepEffort = value.model === id && value.effort ? value.effort : m.default_effort;
    onChange({ model: id, effort: m.efforts.length ? keepEffort : null });
    // Si el modelo tiene niveles, el menú sigue abierto para elegir el esfuerzo.
    if (!m.efforts.length) close(true);
  };

  const pickEffort = (id: string) => {
    if (!current) return;
    onChange({ model: current.id, effort: id });
    close(true);
  };

  const itemCls = (checked: boolean) =>
    `flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left outline-none transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:ring-2 focus-visible:ring-ring ${
      checked ? 'bg-muted/60' : ''
    }`;

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Modelo: ${label}. Cambiar modelo y esfuerzo`}
        title={isAuto ? `Auto — ${info.auto.description}` : `${label} — cambiar modelo`}
        className={`flex h-10 w-10 shrink-0 items-center justify-center gap-1.5 rounded-xl border text-sm font-medium transition-colors disabled:opacity-60 ${compact ? '' : 'lg:w-auto lg:max-w-[14rem] lg:justify-start lg:px-3'} ${
          isAuto
            ? 'border-border bg-card text-muted-foreground hover:bg-muted'
            : 'border-primary/50 bg-primary/10 text-primary'
        }`}
      >
        {current ? <ProviderLogo provider={current.provider} size={16} /> : <Sparkles size={15} aria-hidden className="shrink-0" />}
                <span className={compact ? 'sr-only' : 'hidden truncate lg:inline'}>{label}</span>
        <ChevronDown size={14} aria-hidden className={`hidden shrink-0 transition-transform ${compact ? '' : 'lg:block'} ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Modelo y esfuerzo"
          onKeyDown={onMenuKeyDown}
          className="absolute bottom-full left-0 z-30 mb-2 max-h-[min(75vh,36rem)] w-[min(20rem,calc(100vw-1.5rem))] overflow-y-auto rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-xl"
        >
          <div role="group" aria-label="Modelo">
            <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Modelo</p>
            <button
              type="button"
              role="menuitemradio"
              aria-checked={isAuto}
              tabIndex={-1}
              onClick={() => pickModel('auto')}
              className={itemCls(isAuto)}
            >
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary">
                {isAuto && <Check size={15} aria-hidden />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-foreground">{info.auto.label || 'Auto'}</span>
                <span className="block text-xs text-muted-foreground">
                  {info.auto.description}
                  {info.auto.resolves_to_label ? ` Hoy: ${info.auto.resolves_to_label}.` : ''}
                </span>
              </span>
            </button>
            {groupModels(info).map((g) => (
              <div key={g.key} role="group" aria-label={g.label} className="mt-1 border-t border-border/70 pt-1">
                <p className="flex items-center gap-2 px-2.5 pb-1 pt-1.5 text-xs font-semibold text-foreground">
                  <ProviderLogo provider={g.provider} size={16} muted={!g.models.length} />
                  {g.label}
                  {!g.models.length && (
                    <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Próximamente
                    </span>
                  )}
                </p>
                {g.models.map((m) => {
                  const checked = !isAuto && value.model === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={checked}
                      tabIndex={-1}
                      onClick={() => pickModel(m.id)}
                      className={itemCls(checked)}
                    >
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary">
                        {checked && <Check size={15} aria-hidden />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-foreground">{m.label}</span>
                        <span className="block text-xs text-muted-foreground">{m.description}</span>
                      </span>
                    </button>
                  );
                })}
                {!g.models.length && (
                  <p className="px-2.5 pb-1.5 text-xs text-muted-foreground" aria-disabled="true">
                    Aún no disponible para elegir.
                  </p>
                )}
              </div>
            ))}
          </div>

          {current && efforts.length > 0 && (
            <div role="group" aria-label="Esfuerzo" className="mt-1 border-t border-border pt-1">
              <p className="flex items-center gap-1.5 px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Gauge size={12} aria-hidden /> Esfuerzo
              </p>
              {efforts.map((e) => {
                const checked = value.effort === e.id;
                return (
                  <button
                    key={e.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={checked}
                    tabIndex={-1}
                    onClick={() => pickEffort(e.id)}
                    className={itemCls(checked)}
                  >
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary">
                      {checked && <Check size={15} aria-hidden />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground">{e.label}</span>
                      <span className="block text-xs text-muted-foreground">{e.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ModelPicker;
